import { validationSchema } from "Pages/SchedulePartner/schemas";

import { createContext, ReactNode, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  createScheduleAPI,
  uploadFilesPartners,
} from "Requests/SchedulePartners";
import { createToast } from "Utils/toastFunc";
import * as Yup from "yup";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useHistory } from "react-router";

interface ScheduleContextProps {
  steps: {
    registerPersonalInfo: boolean;
    selectDate: boolean;
    checkCep: boolean;
  };
  personalInfo: FormValues | null;
  appointment: {
    query_date: string;
    query_hour: string;
  };
  loadingCreation: boolean;
  setPersonalInfoFn: (data: any) => void;
  setAppointment: (data: any) => void;
  setAppointmentInfo: (data: any) => void;
  setFiles: (file: File) => void;
  uploadedFiles: File[] | [];
  deletFilesToUpload: (index: number) => void;
  createSchedule: () => void;
  advanceToPersonalInfoForm: (cep: string, appointmentId?: number) => void;
  cep: string;
  appointmentId: number | null;
  resetSchedule: () => void;
}

export const ScheduleContext = createContext<ScheduleContextProps | null>(null);
export type FormValues = Yup.InferType<typeof validationSchema>;

export const ScheduleProvider = ({ children }: { children: ReactNode }) => {
  const [steps, setSteps] = useState({
    registerPersonalInfo: false,
    checkCep: true,
    selectDate: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [personalInfo, setPersonalInfo] = useState<FormValues | null>(null);
  const [cep, setCep] = useState("");
  const [appointment, setAppointment] = useState({
    query_date: "",
    query_hour: "",
  });
  const [appointmentId, setAppointmentId] = useState<number | null>(null);

  const client = useQueryClient();

  const history = useHistory();

  const createScheduleMutation = useMutation({
    mutationFn: createScheduleAPI,
  });

  const uploadFilesMutation = useMutation({
    mutationFn: uploadFilesPartners,
  });

  const resetSchedule = () => {
    setSteps({
      registerPersonalInfo: false,
      selectDate: false,
      checkCep: true,
    });
    setUploadedFiles([]);
    setPersonalInfo(null);
    setAppointment({
      query_date: "",
      query_hour: "",
    });
    client.removeQueries({ queryKey: ["dates"] });
    client.removeQueries({ queryKey: ["hours"] });
  };

  const advanceToPersonalInfoForm = (
    zipcode: string,
    appointmentTypeId?: number
  ) => {
    if (appointmentTypeId) setAppointmentId(appointmentTypeId);
    setCep(zipcode);
    setSteps({
      registerPersonalInfo: true,
      selectDate: false,
      checkCep: false,
    });
  };

  const setPersonalInfoFn = (data: FormValues) => {
    setPersonalInfo(data);
    setSteps({
      registerPersonalInfo: false,
      selectDate: true,
      checkCep: false,
    });
  };

  const setFiles = (file: File) => {
    setUploadedFiles((prev) => [...prev, file]);
  };

  const setAppointmentInfo = (data: any) => {
    setAppointment((prev) => ({ ...prev, ...data }));
  };

  const createRequestObj = () => {
    if (personalInfo) {
      const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(
        personalInfo.phone
      ).split(" ");

      const phone = `${n1}${n2}`;

      return {
        datetime: appointment.query_hour,
        fullName: personalInfo.name,
        ddi,
        ddd,
        phone,
        email: personalInfo.email,
        cpf: personalInfo.cpf,
        birthDate: String(personalInfo.birthDate),
        weight: personalInfo.weight ? personalInfo.weight : "n/a",
        height: personalInfo.height ? personalInfo.height : "n/a",
        appointmentType: appointmentId,
        mothersFullName: personalInfo.motherName
          ? personalInfo.motherName
          : "n/a",
        gender: personalInfo.gender,
        address: {
          zipcode: personalInfo.cep,
          street: personalInfo.street,
          number: personalInfo.number,
          complement: personalInfo.complement,
          neighborhood: personalInfo.neighborhood,
          city: personalInfo.city,
          state: personalInfo.state,
        },
        observation: personalInfo.obs,
        exams: [],
      };
    }
  };

  const deletFilesToUpload = (indexToRemove: number) => {
    const files = uploadedFiles.filter((_, index) => index !== indexToRemove);
    setUploadedFiles(files);
  };

  const uploadFiles = async () => {
    try {
      if (uploadedFiles.length) {
        const responses = await Promise.all(
          uploadedFiles.map((file) => {
            const formData = new FormData();
            formData.append("file", file);
            return uploadFilesMutation.mutateAsync(formData);
          })
        );

        return responses.map((response) => ({ id: response.id }));
      }
    } catch (error) {
      throw new Error("Não foi possível fazer o upload dos documentos");
    }
  };

  const createSchedule = async () => {
    try {
      const filesId = await uploadFiles();
      const clientInfo = createRequestObj() as any;

      if (!clientInfo?.address.complement)
        delete clientInfo?.address.complement;

      if (!clientInfo?.observation) delete clientInfo?.observation;

      if (personalInfo && clientInfo) {
        let data = {
          ...clientInfo,
        };

        if (filesId) {
          data = {
            ...data,
            medicalClaimsFiles: filesId,
          };
        }

        await createScheduleMutation.mutateAsync({
          data,
        });

        createToast("success", "Agendamento realizado com sucesso");
        client.removeQueries({ queryKey: ["partners-orders"] });
        client.removeQueries({ queryKey: ["checkcep"] });
        history.push("/listar-agendamentos-parceiros");
        return;
      }

      throw new Error("Informações pessoais não cadastradas");
    } catch (errors: any) {
      const error = errors?.response?.data?.errors[0].message;

      if (error) {
        createToast("error", error);
        return;
      }

      if (errors?.message) {
        createToast("error", errors.message);
      }
    }
  };

  useEffect(() => {
    return () => resetSchedule();
  }, []);

  const values = useMemo(
    () => ({
      steps,
      personalInfo,
      appointment,
      setPersonalInfoFn,
      setAppointment,
      setAppointmentInfo,
      setFiles,
      uploadedFiles,
      deletFilesToUpload,
      createSchedule,
      cep,
      loadingCreation:
        createScheduleMutation.isLoading || uploadFilesMutation.isLoading,
      advanceToPersonalInfoForm,
      appointmentId,
      resetSchedule,
    }),
    [
      steps,
      personalInfo,
      appointment,
      uploadedFiles,
      createScheduleMutation.isLoading,
      uploadFilesMutation.isLoading,
      cep,
      appointmentId,
    ]
  );

  return (
    <ScheduleContext.Provider value={values}>
      {children}
    </ScheduleContext.Provider>
  );
};
